import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

export type ReactionCounts = Record<string, number>;
export type UserReactions = Set<string>;

interface State {
  counts: ReactionCounts;
  mine: UserReactions;
  loading: boolean;
}

const empty: State = { counts: {}, mine: new Set(), loading: true };

/** Reactions for a single book. */
export const useBookReactions = (bookId: string | undefined) => {
  const { user } = useAuth();
  const [state, setState] = useState<State>(empty);

  const refresh = useCallback(async () => {
    if (!bookId) return;
    setState((s) => ({ ...s, loading: true }));
    const { data: rows } = await supabase
      .from("book_reactions" as never)
      .select("reaction, user_id")
      .eq("book_id", bookId);
    const counts: ReactionCounts = {};
    const mine = new Set<string>();
    for (const r of (rows ?? []) as Array<{ reaction: string; user_id: string }>) {
      counts[r.reaction] = (counts[r.reaction] ?? 0) + 1;
      if (user && r.user_id === user.id) mine.add(r.reaction);
    }
    setState({ counts, mine, loading: false });
  }, [bookId, user]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = useCallback(
    async (reaction: string) => {
      if (!user) {
        toast.error("Sign in to react");
        return;
      }
      if (!bookId) return;
      const had = state.mine.has(reaction);
      // optimistic
      setState((s) => {
        const nextMine = new Set(s.mine);
        const counts = { ...s.counts };
        if (had) {
          nextMine.delete(reaction);
          counts[reaction] = Math.max(0, (counts[reaction] ?? 1) - 1);
        } else {
          nextMine.add(reaction);
          counts[reaction] = (counts[reaction] ?? 0) + 1;
        }
        return { ...s, mine: nextMine, counts };
      });
      if (had) {
        const { error } = await supabase
          .from("book_reactions" as never)
          .delete()
          .eq("user_id", user.id)
          .eq("book_id", bookId)
          .eq("reaction", reaction);
        if (error) { toast.error("Could not remove reaction"); refresh(); }
      } else {
        const { error } = await supabase
          .from("book_reactions" as never)
          .insert({ user_id: user.id, book_id: bookId, reaction } as never);
        if (error) { toast.error("Could not save reaction"); refresh(); return; }
        trackEvent("book_tbr", { book_id: bookId, reaction, kind: "reaction" });
      }
    },
    [bookId, refresh, state.mine, user],
  );

  return { ...state, toggle, refresh };
};

/** Reaction counts across many books at once (for feed cards). */
export const useReactionCountsForBooks = (bookIds: string[]) => {
  const [counts, setCounts] = useState<Record<string, ReactionCounts>>({});
  const sig = bookIds.slice().sort().join(",");
  const lastSig = useRef("");

  useEffect(() => {
    if (!sig || sig === lastSig.current) return;
    lastSig.current = sig;
    let active = true;
    (async () => {
      const { data } = await supabase
        .from("book_reactions" as never)
        .select("book_id, reaction")
        .in("book_id", bookIds);
      if (!active) return;
      const next: Record<string, ReactionCounts> = {};
      for (const r of (data ?? []) as Array<{ book_id: string; reaction: string }>) {
        next[r.book_id] = next[r.book_id] ?? {};
        next[r.book_id][r.reaction] = (next[r.book_id][r.reaction] ?? 0) + 1;
      }
      setCounts(next);
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  return counts;
};

/** Returns the current user's reaction tallies summed across all books. */
export const useMyReactionTallies = () => {
  const { user } = useAuth();
  const [tallies, setTallies] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!user) { setTallies({}); return; }
    let active = true;
    (async () => {
      const { data } = await supabase
        .from("book_reactions" as never)
        .select("reaction")
        .eq("user_id", user.id);
      if (!active) return;
      const t: Record<string, number> = {};
      for (const r of (data ?? []) as Array<{ reaction: string }>) {
        t[r.reaction] = (t[r.reaction] ?? 0) + 1;
      }
      setTallies(t);
    })();
    return () => { active = false; };
  }, [user]);
  return tallies;
};
